import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useGlobalStateContext } from "../../context/globalState";
import TextLabel from "../TextLabel";
import { CONSTANTS } from "../../CONSTANTS";

const StatusBox: React.FC<{
  onStatusChange: (status: "active" | "inactive" | "blocked") => void;
  initialStatus?: "active" | "inactive" | "blocked";
}> = ({ onStatusChange, initialStatus = "active" }) => {
  // Global Context state
  const { blockReason, setBlockReason, setStatusEndDate, statusEndDate } =
    useGlobalStateContext();

  const [selectedStatus, setSelectedStatus] = useState<
    "active" | "inactive" | "blocked"
  >(initialStatus.toLowerCase() as "active" | "inactive" | "blocked");
  const [prevStatus, setPrevStatus] = useState<
    "active" | "inactive" | "blocked"
  >(initialStatus.toLowerCase() as "active" | "inactive" | "blocked");

  const [openModal, setOpenModal] = useState(false);
  const [blockReasonMessage, setBlockReasonMessage] = useState(""); // Local state for block reason message
  const [prevBlockReasonMessage, setPrevBlockReasonMessage] = useState("");
  const [inactiveDuration, setInactiveDuration] = useState<
    "indefinitely" | "until"
  >("indefinitely"); // Local state to track inactive duration type
  const [inactiveUntilDate, setInactiveUntilDate] = useState(
    statusEndDate || "",
  ); // Initialize with statusEndDate from context
  const [reasonError, setReasonError] = useState("");
  const [isUntilDateError, setIsUntilDateError] = useState(false);
  const [prevInactiveDuration, setPrevInactiveDuration] = useState<
    "indefinitely" | "until"
  >(inactiveDuration); // Local state to track previous inactive duration type

  const { t } = useTranslation();

  // Sync with initialStatus when it changes
  useEffect(() => {
    setSelectedStatus(
      initialStatus.toLowerCase() as "active" | "inactive" | "blocked",
    );
  }, [initialStatus]);

  useEffect(() => {
    if (statusEndDate && statusEndDate !== "indefinitely") {
      setInactiveDuration("until");
      setInactiveUntilDate(new Date(statusEndDate).toISOString().split("T")[0]); // Format date for input
    } else {
      setInactiveDuration("indefinitely");
      setInactiveUntilDate("");
    }
  }, [statusEndDate]);

  useEffect(() => {
    setBlockReasonMessage(blockReason || ""); // Sync local state with context when blockReason changes
  }, [blockReason]);

  // Handle modal submission for blocked status
  const handleModalSubmit = () => {
    // Reset error states
    setReasonError("");
    setIsUntilDateError(false);

    if (blockReasonMessage.trim() === "") {
      setReasonError(t(`adminDashboard.form.statusCardProps.reasonRequired`));
      return; // Do not proceed if reason is empty
    }
    if (selectedStatus === "inactive") {
      if (inactiveDuration === "until" && !inactiveUntilDate) {
        setIsUntilDateError(true);
        return; // Do not proceed if until date is required but not provided
      }
      setStatusEndDate(
        inactiveDuration === "indefinitely"
          ? "indefinitely"
          : inactiveUntilDate,
      ); // Save end date to context
    } else {
      setStatusEndDate(""); // Clear end date for non-inactive statuses
    }
    setBlockReason(blockReasonMessage); // Save block reason to context
    setPrevBlockReasonMessage(""); // Clear previous block reason
    setOpenModal(false); // Close the modal
    setReasonError(""); // Clear reason error
    setIsUntilDateError(false); // Clear until date error
    onStatusChange(selectedStatus); // Notify parent component about status change
  };

  // Handle block reason change
  const handleBlockReason = (reason: string) => {
    if (reason && reason.length > 100) {
      setReasonError(
        t(`adminDashboard.form.statusCardProps.reasonCharLimitExceeded`),
      );
      return; // Do not allow more than 100 characters
    }
    if (reason && reason.trim() !== "") {
      setReasonError("");
    }
    setBlockReasonMessage(reason); // Sync with local state
  };

  // Handle status change (other than "blocked")
  const handleStatusChange = (key: "active" | "inactive" | "blocked") => {
    if (key === "active" && selectedStatus === "active") return; // No change if already active
    // @ts-ignore - Ignore type error for empty string assignment
    if (key === "active" && selectedStatus === "") {
      setSelectedStatus(key);
      onStatusChange(key);
      return;
    }
    setPrevStatus(selectedStatus); // Snapshot current status before opening modal
    setPrevBlockReasonMessage(blockReasonMessage); // Snapshot current reason before opening modal
    setPrevInactiveDuration(inactiveDuration); // Snapshot current inactive duration type before opening modal
    if (selectedStatus !== key) {
      setBlockReasonMessage(""); // Clear block reason when changing status
      setSelectedStatus(key);
      setInactiveUntilDate(""); // Clear inactive until date when changing status
    }
    setOpenModal(true); // Open modal to provide status reason
  };

  const handleModalClose = () => {
    setOpenModal(false);
    prevStatus !== selectedStatus && setSelectedStatus(prevStatus); // Revert to previous status if modal is closed without saving
    prevBlockReasonMessage !== blockReasonMessage &&
      setBlockReasonMessage(prevBlockReasonMessage); // Revert to previous block reason if modal is closed without saving
    prevInactiveDuration === "until" &&
      statusEndDate &&
      statusEndDate !== "indefinitely" &&
      setInactiveUntilDate(
        new Date(statusEndDate).toISOString().split("T")[0] || "",
      ); // Revert to previous inactive until date if modal is closed without saving
    prevInactiveDuration === "indefinitely" && setInactiveUntilDate(""); // Clear inactive until date if status end date is indefinitely
    setInactiveDuration(prevInactiveDuration); // Revert to previous inactive duration type if modal is closed without saving
    setReasonError(""); // Clear reason error
    setIsUntilDateError(false); // Clear until date error
  };

  // Status options with corresponding icons
  const statuses = [
    { key: "active", label: "Active", icon: CheckCircleIcon, color: "#4CAF50" },
    { key: "inactive", label: "Inactive", icon: CancelIcon, color: "#FFEB3B" },
    {
      key: "blocked",
      label: "Blocked",
      icon: RemoveCircleIcon,
      color: "#F44336",
    },
  ] as const;

  const getProvideReasonText = (status: "active" | "inactive" | "blocked") => {
    switch (status) {
      case "blocked":
        return t(`adminDashboard.form.statusCardProps.provideBlockReason`);
      case "inactive":
        return t(`adminDashboard.form.statusCardProps.provideInactiveReason`);
      case "active":
        return t(`adminDashboard.form.statusCardProps.provideUnblockReason`);
      default:
        return "";
    }
  };

  const isUntilDateRequired =
    selectedStatus === "inactive" && inactiveDuration === "until";

  const onUntilDateUpdate = (date: string) => {
    if (date && date.trim() !== "") {
      setIsUntilDateError(false);
      setInactiveUntilDate(date);
    }
  };

  const showCharLimitWarning = (blockReasonMessage: string) => {
    return `${blockReasonMessage.length}/100 ${t(`adminDashboard.form.statusCardProps.characters`)}${blockReasonMessage.length === 100 ? ` - ${t(`adminDashboard.form.statusCardProps.charLimitReached`)}` : ""}`;
  };

  return (
    <>
      <Card
        sx={{
          width: 103,
          marginLeft: "4px",
          padding: "2px",
          borderRadius: "8px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <TextLabel
          type="regularText"
          variant="subtitle1"
          mb={1}
          color={CONSTANTS.COLORS.TEXT_GRAY}
        >
          {t(`adminDashboard.form.statusCardProps.Status`)}
        </TextLabel>

        {statuses.map(({ key, label, icon: Icon, color }) => (
          <Box
            key={key}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              padding: "8px",
              borderRadius: 1,
              backgroundColor:
                selectedStatus === key ? "#F5F5F5" : "transparent",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#EEEEEE" },
            }}
            onClick={() => handleStatusChange(key)} // Handle click to select status
          >
            <Icon sx={{ color: selectedStatus === key ? color : "#D3D3D3" }} />
            <TextLabel type="smallText" color="text.primary">
              {t(`adminDashboard.form.statusCardProps.${label}`) || label}
            </TextLabel>
          </Box>
        ))}
      </Card>

      {/* Blocked Reason Modal */}
      <Dialog
        open={openModal}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedStatus === "inactive" && (
            <TextLabel type="tabHeader" sxStyles={{ fontFamily: "sans-serif" }}>
              {t(`adminDashboard.form.statusCardProps.inactiveTitle`)}
            </TextLabel>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedStatus === "inactive" && (
            <Box display="flex" flexDirection="column" mb={2}>
              <RadioGroup
                row
                value={inactiveDuration}
                aria-label={t(
                  `adminDashboard.form.statusCardProps.inactivityRadioGroupAriaLabel`,
                  "inactivity duration",
                )}
                onChange={(e) => {
                  const value = e.target.value as "indefinitely" | "until";
                  setInactiveDuration(value);
                  if (value === "indefinitely") {
                    setInactiveUntilDate("");
                    setIsUntilDateError(false); // Reset error state when changing duration
                  }
                }}
              >
                <FormControlLabel
                  value="indefinitely"
                  control={<Radio size="small" />}
                  label={t(
                    `adminDashboard.form.statusCardProps.inactivityRadioLabels.indefinitely`,
                    "Indefinitely",
                  )}
                  aria-label={t(
                    `adminDashboard.form.statusCardProps.inactivityRadioLabels.indefinitely`,
                    "Indefinitely",
                  )}
                />
                <Box display="flex" alignItems="center" gap={1}>
                  <FormControlLabel
                    value="until"
                    control={<Radio size="small" />}
                    label={t(
                      `adminDashboard.form.statusCardProps.inactivityRadioLabels.until`,
                      "Until",
                    )}
                    aria-label={t(
                      `adminDashboard.form.statusCardProps.inactivityRadioLabels.until`,
                      "Until",
                    )}
                  />
                  <TextField
                    type="date"
                    size="small"
                    aria-label={t(
                      `adminDashboard.form.statusCardProps.inactivityDueDateAriaLabel`,
                      "inactivity due date",
                    )}
                    value={inactiveUntilDate}
                    onChange={(e) => onUntilDateUpdate(e.target.value)}
                    disabled={inactiveDuration !== "until"}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split("T")[0] }}
                    required={isUntilDateRequired}
                    error={isUntilDateError}
                    helperText={
                      isUntilDateError
                        ? t(
                            `adminDashboard.form.statusCardProps.dateRequired`,
                            "Date is required",
                          )
                        : ""
                    }
                  />
                </Box>
              </RadioGroup>
            </Box>
          )}
          <TextLabel type="tabHeader" sxStyles={{ fontFamily: "sans-serif" }}>
            {getProvideReasonText(selectedStatus)}
          </TextLabel>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            variant="outlined"
            value={blockReasonMessage} // Use local state for the block reason
            onChange={(e) => handleBlockReason(e.target.value)} // Update block reason on change
            error={!!reasonError}
            helperText={
              reasonError
                ? reasonError
                : showCharLimitWarning(blockReasonMessage) // Show character count and limit warning
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} color="primary">
            <TextLabel type="bluButtonText" textTransform="capitalize">
              {t(`adminDashboard.form.button.cancel`)}
            </TextLabel>
          </Button>
          <Button onClick={handleModalSubmit} color="primary">
            <TextLabel type="bluButtonText" textTransform="capitalize">
              {t(`adminDashboard.form.button.save`)}
            </TextLabel>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StatusBox;
